import {chain, InputProps} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';

import {showWarningNotification} from '../../providers/notifications-center/show-notification';
import styles from './editable-title.module.css';

const EMPTY_NAME_WARNING = 'This field cannot be empty';

const isBlank = (text: string) => text.trim().length === 0;

interface Props extends InputProps {
    value: string;
    onSubmitValue: (name: string) => void;
    onCancel: () => void;
}

/** Input for editable title */
export const EditModeInput = React.forwardRef<HTMLInputElement, Props>(
    function EditModeInputComponent(props, ref) {
        const {value, onSubmitValue, onCancel, ...inputProps} = props;

        const [inputValue, setInputValue] = React.useState(value);

        const handleSave = (nextName: string) => {
            const normalizedName = nextName.trim().replace(/\s+/g, ' ');

            if (normalizedName !== value) {
                onSubmitValue(normalizedName);
            } else {
                onCancel();
            }
        };

        const handleResetInputValue = () => {
            setInputValue(value);
            onCancel();
        };

        const submitOrWarn = () => {
            if (isBlank(inputValue)) {
                showWarningNotification(EMPTY_NAME_WARNING);

                return false;
            }

            handleSave(inputValue);

            return true;
        };

        const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
            if (!submitOrWarn()) {
                handleResetInputValue();
            }
        };

        const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
            switch (event.key) {
                case 'Escape':
                    handleResetInputValue();
                    break;
                case 'Enter':
                    submitOrWarn();
                    break;
                default:
            }
        };

        const handleChangeInput: React.ChangeEventHandler<HTMLInputElement> = (event) => {
            setInputValue(event.target.value);
        };

        return (
            <input
                autoFocus
                ref={ref}
                data-qa="title-input"
                {...inputProps}
                className={classNames(styles.input, inputProps.className)}
                value={inputValue}
                onBlur={chain(handleBlur, inputProps.onBlur)}
                onKeyUp={chain(handleKeyUp, inputProps.onKeyUp)}
                onChange={chain(handleChangeInput, inputProps.onChange)}
            />
        );
    }
);
